import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { API, StatCard, Field, inputCls, selectCls, DollarSign, Users, TrendingUp, Gauge, ArrowDownUp, Wallet, Shield, Clock } from './shared';
import { Link2, XCircle, ChevronDown } from 'lucide-react';

export default function LenderPoolTab() {
  const [stats, setStats] = useState(null);
  const [creditLines, setCreditLines] = useState([]);
  const [agents, setAgents] = useState([]);
  const [guardStatus, setGuardStatus] = useState(null);

  // Extend Credit form
  const [lAddr, setLAddr] = useState('');
  const [aAddr, setAAddr] = useState('');
  const [limit, setLimit] = useState('');
  const [feePct, setFeePct] = useState('5.0');
  const [maxDraw, setMaxDraw] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Revoke form
  const [revLender, setRevLender] = useState('');
  const [revAgent, setRevAgent] = useState('');

  // Expanded credit line
  const [expanded, setExpanded] = useState(null);

  const refresh = useCallback(() => {
    fetch(`${API}/api/agent-credit/pool/stats`).then(r => r.json()).then(setStats).catch(() => {});
    fetch(`${API}/api/agent-credit/pool/credit-lines?limit=20`).then(r => r.json()).then(d => setCreditLines(d.credit_lines || [])).catch(() => {});
    fetch(`${API}/api/agent-credit/agents`).then(r => r.json()).then(d => setAgents((d.agents || []).slice(0, 10))).catch(() => {});
    fetch(`${API}/api/withdrawal-guard/status`).then(r => r.json()).then(setGuardStatus).catch(() => {});
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const extendCredit = async (e) => {
    e.preventDefault();
    setLoading(true); setResult(null);
    try {
      const res = await fetch(`${API}/api/agent-credit/pool/deposit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lender_address: lAddr.trim(),
          agent_address: aAddr.trim(),
          credit_limit: parseFloat(limit),
          service_fee_pct: parseFloat(feePct),
          max_single_draw: maxDraw ? parseFloat(maxDraw) : 0,
        }),
      });
      const data = await res.json();
      setResult(res.ok ? { type: 'success', msg: data.message } : { type: 'error', msg: data.detail });
      if (res.ok) refresh();
    } catch { setResult({ type: 'error', msg: 'Network error' }); }
    finally { setLoading(false); }
  };

  const revoke = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/agent-credit/pool/revoke`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lender_address: revLender.trim(), agent_address: revAgent.trim() }),
      });
      const data = await res.json();
      setResult(res.ok ? { type: 'success', msg: data.message } : { type: 'error', msg: data.detail });
      if (res.ok) refresh();
    } catch { setResult({ type: 'error', msg: 'Network error' }); }
  };

  return (
    <div>
      {/* Regulatory Notice */}
      <div className="mb-6 p-3 rounded-xl bg-amber-500/5 border border-amber-500/20 text-xs text-amber-400/80">
        <strong>Direct Credit Marketplace:</strong> Each lender makes individual credit decisions for specific agents.
        No pooled capital. Lenders set their own terms, limits, and service fees per agent.
        This is a bilateral credit facility, not a collective investment.
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <StatCard icon={DollarSign} label="Total Credit Extended" value={`$${(stats.total_credit_extended || stats.tvl || 0).toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}`} color="green" />
          <StatCard icon={Users} label="Credit Providers" value={stats.lender_count} color="blue" />
          <StatCard icon={TrendingUp} label="Service Fees Earned" value={`$${(stats.total_fees_earned || stats.total_yield_distributed || 0).toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}`} color="purple" />
          <StatCard icon={Gauge} label="Utilization" value={`${stats.utilization_rate}%`} sub={`${stats.active_credit_lines} active lines`} color="orange" />
        </div>
      )}

      {/* Withdrawal Rate Limit */}
      {guardStatus && !guardStatus.error && (
        <div className="mb-8 rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-4" data-testid="withdrawal-guard-banner">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-4 h-4 text-emerald-400" />
            <span className="text-sm font-semibold text-emerald-400">On-Chain Withdrawal Rate Limit</span>
            {guardStatus.enforcing ? (
              <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 font-medium">ENFORCING</span>
            ) : (
              <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-yellow-500/20 border border-yellow-500/30 text-yellow-400 font-medium">MONITORING</span>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div><span className="text-gray-500 block">Epoch Limit</span><span className="font-mono text-white">${guardStatus.epoch_limit_usd?.toLocaleString()}</span></div>
            <div><span className="text-gray-500 block">Withdrawn This Epoch</span><span className="font-mono text-white">${guardStatus.epoch_withdrawn_usd?.toLocaleString()}</span></div>
            <div><span className="text-gray-500 block">Remaining Capacity</span><span className="font-mono text-emerald-400">${guardStatus.remaining_capacity_usd?.toLocaleString()}</span></div>
            <div><span className="text-gray-500 block flex items-center gap-1"><Clock className="w-3 h-3" /> Resets In</span>
              <span className="font-mono text-white">
                {guardStatus.epoch_time_remaining_seconds > 3600
                  ? `${Math.floor(guardStatus.epoch_time_remaining_seconds / 3600)}h ${Math.floor((guardStatus.epoch_time_remaining_seconds % 3600) / 60)}m`
                  : `${Math.floor(guardStatus.epoch_time_remaining_seconds / 60)}m`}
              </span>
            </div>
          </div>
          <div className="mt-3 h-1.5 bg-white/5 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${guardStatus.epoch_utilization_pct > 80 ? 'bg-red-500' : guardStatus.epoch_utilization_pct > 50 ? 'bg-yellow-500' : 'bg-emerald-500'}`}
              style={{ width: `${Math.min(guardStatus.epoch_utilization_pct, 100)}%` }} />
          </div>
        </div>
      )}

      {/* Forms: Extend Credit + Revoke */}
      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <div className="rounded-2xl border border-green-500/20 bg-green-500/[0.03] p-6">
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2"><Link2 className="w-5 h-5 text-green-400" /> Extend Credit to Agent</h3>
          <form onSubmit={extendCredit} className="space-y-3">
            <Field label="Your Lender Address"><input value={lAddr} onChange={e => setLAddr(e.target.value)} placeholder="0x..." className={inputCls} required data-testid="credit-lender-address" /></Field>
            <Field label="Agent Address">
              {agents.length > 0 ? (
                <select value={aAddr} onChange={e => setAAddr(e.target.value)} className={selectCls} required data-testid="credit-agent-address">
                  <option value="">Select an agent...</option>
                  {agents.map(a => (
                    <option key={a.agent_address} value={a.agent_address}>
                      {a.name} ({a.agent_address?.slice(0,8)}...) — ACS: {a.acs_score || 'N/A'}
                    </option>
                  ))}
                </select>
              ) : (
                <input value={aAddr} onChange={e => setAAddr(e.target.value)} placeholder="0xAgent..." className={inputCls} required data-testid="credit-agent-address" />
              )}
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Credit Limit (USD)"><input value={limit} onChange={e => setLimit(e.target.value)} placeholder="5000" type="number" min="0" step="0.01" className={inputCls} required data-testid="credit-limit" /></Field>
              <Field label="Service Fee (% annual)"><input value={feePct} onChange={e => setFeePct(e.target.value)} placeholder="5.0" type="number" min="0" max="25" step="0.1" className={inputCls} data-testid="credit-fee" /></Field>
            </div>
            <Field label="Max Per-Task Draw (USD, 0 = no limit)"><input value={maxDraw} onChange={e => setMaxDraw(e.target.value)} placeholder="0" type="number" min="0" step="0.01" className={inputCls} data-testid="credit-max-draw" /></Field>
            <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:brightness-110" data-testid="credit-extend-btn">
              {loading ? 'Extending...' : 'Extend Credit Line'}
            </Button>
          </form>
        </div>

        <div className="rounded-2xl border border-orange-500/20 bg-orange-500/[0.03] p-6">
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2"><XCircle className="w-5 h-5 text-orange-400" /> Revoke Credit Line</h3>
          <form onSubmit={revoke} className="space-y-4">
            <Field label="Your Lender Address"><input value={revLender} onChange={e => setRevLender(e.target.value)} placeholder="0x..." className={inputCls} required data-testid="revoke-lender" /></Field>
            <Field label="Agent Address"><input value={revAgent} onChange={e => setRevAgent(e.target.value)} placeholder="0xAgent..." className={inputCls} required data-testid="revoke-agent" /></Field>
            <Button type="submit" className="w-full bg-gradient-to-r from-orange-600 to-amber-600 hover:brightness-110" data-testid="revoke-btn">
              Revoke Undrawn Credit
            </Button>
          </form>
          <p className="mt-3 text-[10px] text-gray-600">Note: Cannot revoke credit that is currently utilized in active escrows. Credit limit will be reduced to outstanding amount until settlement.</p>
        </div>
      </div>

      {/* Result */}
      {result && (
        <div className={`mb-6 p-4 rounded-lg border ${result.type === 'success' ? 'bg-green-500/10 border-green-500/20 text-green-400' : 'bg-red-500/10 border-red-500/20 text-red-400'} text-sm`} data-testid="credit-action-result">
          {result.msg}
        </div>
      )}

      {/* Active Credit Lines */}
      <div className="rounded-2xl border border-white/5 bg-white/[0.02] overflow-hidden" data-testid="credit-lines-table">
        <div className="px-5 py-4 border-b border-white/5">
          <h3 className="font-semibold flex items-center gap-2"><Link2 className="w-4 h-4 text-green-400" /> Active Credit Lines</h3>
        </div>
        {creditLines.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-xs text-gray-500 uppercase border-b border-white/5">
                <th className="px-4 py-3 text-left">Lender</th>
                <th className="px-4 py-3 text-left">Agent</th>
                <th className="px-4 py-3 text-right">Limit</th>
                <th className="px-4 py-3 text-right">Utilized</th>
                <th className="px-4 py-3 text-right">Fee</th>
                <th className="px-4 py-3 text-right">Earned</th>
                <th className="px-4 py-3 text-right">Escrows</th>
              </tr></thead>
              <tbody className="divide-y divide-white/[0.03]">
                {creditLines.map(l => (
                  <tr key={l.id} className="hover:bg-white/[0.02] cursor-pointer" onClick={() => setExpanded(expanded === l.id ? null : l.id)}>
                    <td className="px-4 py-3 font-mono text-xs">{l.lender_address?.slice(0,8)}...{l.lender_address?.slice(-4)}</td>
                    <td className="px-4 py-3 font-mono text-xs">{l.agent_address?.slice(0,8)}...{l.agent_address?.slice(-4)}</td>
                    <td className="px-4 py-3 text-right font-mono">${l.credit_limit?.toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}</td>
                    <td className="px-4 py-3 text-right font-mono text-amber-400">${l.utilized?.toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}</td>
                    <td className="px-4 py-3 text-right">{l.service_fee_pct}%</td>
                    <td className="px-4 py-3 text-right font-mono text-green-400">${l.total_fees_earned?.toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}</td>
                    <td className="px-4 py-3 text-right">{l.escrow_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-12 text-center">
            <Link2 className="w-8 h-8 text-gray-700 mx-auto mb-3" />
            <p className="text-sm text-gray-600">No credit lines yet. Extend credit to an agent to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
}
